/**
 * API客户端工具类
 * 封装动态获取baseUrl的逻辑，支持Nginx代理和直接访问
 */
import { logger } from './logger';

// 请求配置
const REQUEST_TIMEOUT = 30000; // 30秒超时
const MAX_RETRIES = 3; // 最大重试次数
const RETRY_DELAY = 1000; // 初始重试延迟（毫秒）

/**
 * 延迟函数
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * 带超时的fetch请求
 */
async function fetchWithTimeout(url: string, options: RequestInit, timeout: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('请求超时，请检查网络连接后重试');
    }
    throw error;
  }
}

/**
 * 带重试的请求执行
 */
async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries: number = MAX_RETRIES,
  retryDelay: number = RETRY_DELAY
): Promise<T> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // 如果是最后一次尝试，直接抛出错误
      if (attempt === maxRetries) {
        throw lastError;
      }

      // 判断是否应该重试
      const shouldRetry = shouldRetryRequest(lastError);
      if (!shouldRetry) {
        throw lastError;
      }

      // 指数退避：延迟时间 = 初始延迟 * 2^尝试次数
      const delayTime = retryDelay * Math.pow(2, attempt);
      logger.debug(`请求失败，${delayTime}ms后重试 (${attempt + 1}/${maxRetries})`);
      await delay(delayTime);
    }
  }

  throw lastError || new Error('请求失败');
}

/**
 * 判断是否应该重试请求
 */
function shouldRetryRequest(error: Error): boolean {
  const errorMessage = error.message.toLowerCase();
  
  // 网络错误应该重试
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('timeout')) {
    return true;
  }

  // 5xx服务器错误应该重试
  if (errorMessage.includes('status: 500') || errorMessage.includes('status: 502') || errorMessage.includes('status: 503')) {
    return true;
  }

  // 其他错误不重试
  return false;
}

export class ApiClient {
  /**
   * 获取认证token
   */
  static getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * 获取有效的认证token，如果即将过期则自动刷新
   */
  static async getValidAuthToken(): Promise<string | null> {
    // 动态导入authService以避免循环依赖
    const { authService } = await import('../services/authService');
    return await authService.getValidToken();
  }

  /**
   * 获取默认请求头（包含认证token）
   */
  static getDefaultHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * 获取默认请求头（包含有效的认证token，自动刷新）
   */
  static async getDefaultHeadersWithRefresh(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    const token = await this.getValidAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * 获取后端服务器地址
   */
  static getBaseUrl(): string {
    const protocol = window.location.protocol;
    const host = window.location.hostname;
    const port = window.location.port;
    
    logger.debug('API客户端连接信息:', { host, protocol, port });
    
    // 如果当前是通过开发服务器访问（端口12346），直接访问后端API
    if (port === '12346') {
      const backendUrl = `http://localhost:12345/api`; // 直接访问后端端口12345
      logger.debug('开发环境，直接访问后端API:', backendUrl);
      return backendUrl;
    }
    
    // 如果通过Nginx访问（端口443或8443），使用当前端口
    if (port === '443' || port === '8443' || port === '') {
      const currentUrl = `${protocol}//${host}${port ? ':' + port : ''}/api`;
      logger.debug('生产环境，使用当前端口API:', currentUrl);
      return currentUrl;
    }
    
    // 如果直接访问后端端口（12345），应该通过Nginx代理访问
    if (port === '12345') {
      const nginxUrl = `${protocol}//${host}/api`; // 使用默认端口443
      logger.debug('直接访问后端端口，重定向到Nginx代理API:', nginxUrl);
      return nginxUrl;
    }
    
    // 其他情况，默认使用443端口
    const defaultUrl = `${protocol}//${host}/api`;
    logger.debug('其他情况，使用默认端口443 API:', defaultUrl);
    return defaultUrl;
  }

  /**
   * 通用GET请求（带重试和超时）
   */
  static async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    logger.debug('GET请求:', url);
    logger.debug('请求头:', this.getDefaultHeaders());
    
    return fetchWithRetry(async () => {
      try {
        const response = await fetchWithTimeout(url, {
          method: 'GET',
          headers: {
            ...this.getDefaultHeaders(),
            ...options?.headers,
          },
          ...options,
        }, REQUEST_TIMEOUT);

        logger.debug('响应状态:', response.status, response.statusText);
        logger.debug('响应头:', Object.fromEntries(response.headers.entries()));

        const responseText = await response.text();
        
        if (!response.ok) {
          logger.error('HTTP错误响应:', responseText);
          // 尝试解析错误响应中的 JSON
          let errorData: any = null;
          try {
            errorData = JSON.parse(responseText);
          } catch (e) {
            // 如果不是 JSON，使用原始文本
          }
          
          // 创建一个包含更多信息的错误对象
          // 生产环境不泄露详细错误信息
          const isProduction = process.env.NODE_ENV === 'production';
          const errorMessage = isProduction 
            ? (errorData?.message || `请求失败`)
            : (errorData?.message || `HTTP error! status: ${response.status}`);
          const error = new Error(errorMessage);
          (error as any).status = response.status;
          // 生产环境不暴露详细错误数据
          if (!isProduction) {
            (error as any).data = errorData;
            (error as any).responseText = responseText;
          }
          throw error;
        }

        const data = JSON.parse(responseText);
        logger.debug('响应数据:', data);
        return data;
      } catch (error) {
        logger.error('API请求失败:', error);
        throw error;
      }
    });
  }

  /**
   * 通用POST请求（带重试和超时）
   */
  static async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    logger.debug('POST请求:', url, data);
    logger.debug('请求头:', this.getDefaultHeaders());
    
    return fetchWithRetry(async () => {
      try {
        const response = await fetchWithTimeout(url, {
          method: 'POST',
          headers: {
            ...this.getDefaultHeaders(),
            ...options?.headers,
          },
          body: data ? JSON.stringify(data) : undefined,
          ...options,
        }, REQUEST_TIMEOUT);

        logger.debug('响应状态:', response.status, response.statusText);
        logger.debug('响应头:', Object.fromEntries(response.headers.entries()));

        const responseText = await response.text();
        
        if (!response.ok) {
          logger.error('HTTP错误响应:', responseText);
          // 尝试解析错误响应中的 JSON
          let errorData: any = null;
          try {
            errorData = JSON.parse(responseText);
          } catch (e) {
            // 如果不是 JSON，使用原始文本
          }
          
          // 创建一个包含更多信息的错误对象
          // 生产环境不泄露详细错误信息
          const isProduction = process.env.NODE_ENV === 'production';
          const errorMessage = isProduction 
            ? (errorData?.message || `请求失败`)
            : (errorData?.message || `HTTP error! status: ${response.status}`);
          const error = new Error(errorMessage);
          (error as any).status = response.status;
          // 生产环境不暴露详细错误数据
          if (!isProduction) {
            (error as any).data = errorData;
            (error as any).responseText = responseText;
          }
          throw error;
        }

        const responseData = JSON.parse(responseText);
        logger.debug('响应数据:', responseData);
        return responseData;
      } catch (error) {
        logger.error('API请求失败:', error);
        throw error;
      }
    });
  }

  /**
   * 通用PUT请求
   */
  static async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    logger.debug('PUT请求:', url, data);
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        ...this.getDefaultHeaders(),
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 通用DELETE请求（带重试和超时）
   */
  static async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    logger.debug('DELETE请求:', url);
    logger.debug('请求头:', this.getDefaultHeaders());
    
    return fetchWithRetry(async () => {
      try {
        const response = await fetchWithTimeout(url, {
          method: 'DELETE',
          headers: {
            ...this.getDefaultHeaders(),
            ...options?.headers,
          },
          ...options,
        }, REQUEST_TIMEOUT);

        logger.debug('响应状态:', response.status, response.statusText);

        const responseText = await response.text();
        
        if (!response.ok) {
          logger.error('HTTP错误响应:', responseText);
          // 尝试解析错误响应中的 JSON
          let errorData: any = null;
          try {
            errorData = JSON.parse(responseText);
          } catch (e) {
            // 如果不是 JSON，使用原始文本
          }
          
          // 创建一个包含更多信息的错误对象
          // 生产环境不泄露详细错误信息
          const isProduction = process.env.NODE_ENV === 'production';
          const errorMessage = isProduction 
            ? (errorData?.message || `请求失败`)
            : (errorData?.message || `HTTP error! status: ${response.status}`);
          const error = new Error(errorMessage);
          (error as any).status = response.status;
          // 生产环境不暴露详细错误数据
          if (!isProduction) {
            (error as any).data = errorData;
            (error as any).responseText = responseText;
          }
          throw error;
        }

        const data = JSON.parse(responseText);
        logger.debug('响应数据:', data);
        return data;
      } catch (error) {
        logger.error('API请求失败:', error);
        throw error;
      }
    });
  }
}
